/**
 * GlobalControls — the account/org controls that live in the app's chrome:
 * Units · Upgrade · Admin badge · the avatar menu. (The org block is v1-only —
 * see `showOrg`.)
 *
 * WHY THIS EXISTS. These controls used to be markup inside Header.tsx, which
 * Layout renders once above every page. The v2 chrome (built in PageChrome.tsx;
 * design lineage in `docs/chrome-v2-rollout.md` — the earlier
 * `chrome-onebar-hifi.html` folded them into the row, but the shipped
 * clean-slate design moved the globals to the Sidebar via SidebarAccountZone)
 * deletes the green header — so the same controls render in two places, on two
 * grounds:
 *
 *   variant="dark"   the v1 teal band (Header). Byte-for-byte what shipped.
 *   variant="light"  the v2 one-bar row. Same controls, light ground.
 *
 * Extracting rather than duplicating is the whole point: two copies of the
 * account menu would drift the first time someone adds an item to one of them.
 *
 * WHAT THE VARIANT MAY AND MAY NOT CHANGE. It changes GROUND — colours only.
 * It must never change WHICH controls exist or WHAT they do: Law 4 says nothing
 * is removed, degraded, or hidden without Brian's explicit prior approval, and
 * folding a bar into a row is exactly where things quietly vanish. The single
 * exception is his approved reduction: the user's NAME drops from the light row
 * (the avatar remains, and the name still shows inside the menu). That is a
 * reduction of WORDS, not of capability, and it is recorded in State D's
 * anatomy table.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/useAuthStore';
import { useThemeStore } from '../../store/useThemeStore';
import { usePlan } from '../../lib/usePlan';
import { orgApi } from '../../lib/api';
import { resetAnalytics } from '../../lib/analytics';
import Avatar from './Avatar';
import SuperAdminMenuItems from './SuperAdminMenuItems';

export type GlobalControlsVariant = 'dark' | 'light';

// Dark-mode toggle menu item (N1 dev preview — super admins only until the
// N4 GA once every surface passes the sweep; spec docs/night-shift-mode-spec.md
// §7). Deliberately does NOT close the menu, so flipping back is one click.
function ThemeMenuItem() {
  const { theme, toggleTheme } = useThemeStore();
  const isSuperAdmin = useAuthStore(s => s.isSuperAdmin);
  if (!isSuperAdmin()) return null;
  return (
    <button
      onClick={toggleTheme}
      className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
    >
      {theme === 'dark' ? 'Light mode' : 'Dark mode'}
    </button>
  );
}

/** Ground-dependent classes. Colours only — never structure, never behaviour. */
const SKIN = {
  dark: {
    unitsLabel: 'text-teal-300',
    unitsBorder: 'border-teal-400',
    unitsIdle: 'text-teal-100 hover:bg-teal-800/40',
    unitsOn: 'bg-card text-brand-primary dark:text-brand-accent',
    unitsReadonly: 'text-teal-100 border-teal-700',
    adminIdle: 'border-teal-400 text-teal-100 hover:border-white hover:text-white',
    avatarBtn: 'text-teal-100 hover:text-white',
    avatarRing: 'ring-1 ring-white/25',
  },
  light: {
    unitsLabel: 'text-ink-faint',
    unitsBorder: 'border-brand-primary',
    unitsIdle: 'text-ink-muted hover:bg-wash',
    unitsOn: 'bg-brand-primary text-white',
    unitsReadonly: 'text-ink-muted border-line',
    adminIdle: 'border-line text-ink-muted hover:border-brand-primary hover:text-brand-primary',
    avatarBtn: 'text-ink-muted hover:text-ink',
    avatarRing: 'ring-1 ring-line',
  },
} as const;

interface Props {
  variant?: GlobalControlsVariant;
  /** v1 only. In the one bar the org moves under the Avium logo in the sidebar
   *  (Brian 2026-07-17: it is identity, not a page tool). */
  showOrg?: boolean;
}

export default function GlobalControls({ variant = 'dark', showOrg = false }: Props) {
  const { user, org, logout, viewAsUser, toggleViewAsUser } = useAuthStore();
  const isActualAdmin = user?.role === 'admin';
  const { plan, metricUnit } = usePlan();
  const showUpgrade = isActualAdmin && !viewAsUser && plan?.plan === 'free';
  // The capacity unit (Hours/Points) is org-wide; only an admin (not in user-
  // preview) can flip it. Everyone else sees the active unit read-only.
  const canSetUnit = isActualAdmin && !viewAsUser;
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [savingUnit, setSavingUnit] = useState(false);
  const s = SKIN[variant];

  // The account items are SHORTCUTS into the canonical account settings page
  // (/settings/account — registered in the Settings hub + search via
  // lib/settingsNav.ts). The #hash opens the matching editor on arrival, so
  // it's still one click to the editor, but the destination is a real,
  // linkable settings surface instead of a header-private modal (2026-07-01;
  // the modals themselves now live with AccountSettingsPage).
  const goToAccount = (hash: 'photo' | 'password' | 'role') => {
    setShowMenu(false);
    navigate(`/settings/account#${hash}`);
  };

  async function setUnit(u: 'hours' | 'points') {
    if (u === metricUnit || savingUnit) return;
    setSavingUnit(true);
    try {
      await orgApi.setMetricUnit(u);
      // The unit is org-wide and read SERVER-SIDE by nearly every data surface
      // (Brief signals, Capacity, Reports, Workload, Knowledge Graph). Reload so
      // the WHOLE app reflects the switch at once — reliable, instead of a
      // per-surface refetch that left pages stale until a manual refresh (which
      // itself kept catching the demo-reseed window and throwing transient toasts).
      window.location.reload();
    } catch {
      toast.error('Could not change units');
      setSavingUnit(false);
    }
  }

  const handleLogout = () => {
    resetAnalytics();
    logout();
    navigate('/login');
  };

  return (
    <div className="flex items-center gap-3">
      {showOrg && org && (
        <div className="text-right border-r border-teal-400 pr-3 mr-1">
          <div className="text-teal-300 text-xs leading-none">Organization</div>
          <div className="text-white text-sm font-bold leading-none mt-0.5 uppercase tracking-wide">{org.name}</div>
        </div>
      )}
      {/* Org-wide capacity unit — universally available from every page.
          Admins flip it; others see the active unit. */}
      <div className="flex items-center gap-1.5" title="Capacity unit, org-wide — Hours (tracked time) or Story Points (committed)">
        <span className={`${s.unitsLabel} text-[10px] font-bold tracking-normal hidden sm:inline`}>Units</span>
        {canSetUnit ? (
          <div className={`flex items-center rounded-full border ${s.unitsBorder} overflow-hidden text-xs font-semibold`}>
            {(['hours', 'points'] as const).map(u => (
              <button
                key={u}
                onClick={() => setUnit(u)}
                disabled={savingUnit}
                className={`px-2.5 py-1 transition-colors disabled:opacity-60 ${
                  metricUnit === u ? s.unitsOn : s.unitsIdle
                }`}
              >
                {u === 'hours' ? 'Hours' : 'Points'}
              </button>
            ))}
          </div>
        ) : (
          <span className={`${s.unitsReadonly} text-xs font-semibold px-2 py-1 rounded-full border`}>
            {metricUnit === 'points' ? 'Points' : 'Hours'}
          </span>
        )}
      </div>

      {showUpgrade && (
        <button
          onClick={() => navigate('/admin/billing')}
          title="Upgrade to Team or Business for more seats, Jira sync, and Avium Signals"
          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-brand-spark text-white border border-brand-spark hover:bg-brand-spark-dark hover:border-brand-spark-dark transition-colors"
        >
          <span>⚡</span> Upgrade Plan
        </button>
      )}
      {isActualAdmin && (
        <button
          onClick={toggleViewAsUser}
          title={viewAsUser ? 'Switch back to Admin view' : 'Preview as regular user'}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
            viewAsUser
              ? 'bg-brand-spark border-brand-spark text-white'
              : s.adminIdle
          }`}
        >
          {viewAsUser ? (
            <><span>👤</span> User View — click to return to Admin</>
          ) : (
            <><span>🛡️</span> Admin</>
          )}
        </button>
      )}

      <div className="relative">
        <button
          onClick={() => setShowMenu(v => !v)}
          className={`flex items-center gap-2 ${s.avatarBtn} text-sm transition-colors`}
        >
          <Avatar name={user?.name} avatarUrl={user?.avatarUrl} size={28} className={s.avatarRing} />
          {/* His approved reduction: the name goes from the one bar, not from the
              product — it still reads inside the menu below. Words, not capability. */}
          {variant === 'dark' && <span>{user?.name}</span>}
          <span className="text-xs">▾</span>
        </button>

        {showMenu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
            <div className="absolute right-0 top-9 bg-card rounded-lg shadow-lg border border-line py-1 w-44 z-50">
              {/* The name lives here in BOTH variants — in the light row it is the
                  only place it appears, so it is not optional. */}
              {variant === 'light' && user?.name && (
                <div className="px-4 py-2 border-b border-line">
                  <div className="text-sm font-semibold text-ink truncate">{user.name}</div>
                </div>
              )}
              <button
                onClick={() => goToAccount('photo')}
                className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
              >
                Change profile picture
              </button>
              <button
                onClick={() => goToAccount('password')}
                className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
              >
                Change Password
              </button>
              <button
                onClick={() => goToAccount('role')}
                className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
              >
                Your role
              </button>
              <button
                onClick={() => { setShowMenu(false); navigate('/settings/notifications'); }}
                className="w-full text-left px-4 py-2 text-sm text-ink-muted hover:bg-wash"
              >
                Notifications
              </button>
              <ThemeMenuItem />
              <SuperAdminMenuItems onClose={() => setShowMenu(false)} />
              <hr className="my-1" />
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
